import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '@nirvana/redux/store';
import { PolicyAppState, SectionState, SectionStatusType } from './types';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  clearSectionMask,
  clearSectionMaskActionHandler,
  reset,
  resetActionHandler,
  setInitialTreeState,
  setInitialTreeStateActionHandler,
  setPolicyDataLoaded,
  setPolicyDataLoadedActionHandler,
  setSectionMask,
  setSectionMaskActionHandler,
  setSectionValidity,
  setSectionValidityActionHandler,
} from './actions';
import {
  setIsStatusFullUpdateNeeded,
  setIsStatusFullUpdateNeededActionHandler,
  setIsStatusPathUpdateNeeded,
  setIsStatusPathUpdateNeededActionHandler,
  setPathStatuses,
  setPathStatusesActionHandler,
  setPathToBeUpdatedWithStatus,
  setPathToBeUpdatedWithStatusActionHandler,
} from './statusSlice/actions.status';
import { defaultPatchManagementState } from './customStates/patchManagement/patch-management.slice';
import {
  setAvailableMaintenanceWindowsLoadedForPatch,
  setAvailableMaintenanceWindowsLoadedForPatchActionHandler,
  setIsLoadingForPatchSummaryWindow,
  setIsLoadingForPatchSummaryWindowActionHandler,
  setSelectedIdsForPatchSummaryWindow,
  setSelectedIdsForPatchSummaryWindowActionHandler,
} from './customStates/patchManagement/patch-management.actions';
import { defaultIntegrityMonitorRealTimeState } from './customStates/integrityMonitor/integrity-monitor.slice';
import {
  setCompanyRuleSetsLoadedForIntegrityMonitor,
  setCompanyRuleSetsLoadedForIntegrityMonitorActionHandler,
  setSelectedRuleSetsLoadedForIntegrityMonitor,
  setSelectedRuleSetsLoadedForIntegrityMonitorActionHandler,
} from './customStates/integrityMonitor/integrity-monitor.actions';
import { PolicyContext } from './navigationHeaderSlice/types.navigation';
import {
  setNavigationHeaderSlice,
  setNavigationHeaderSliceActionHandler,
  setPolicyName,
  setPolicyNameActionHandler,
} from './navigationHeaderSlice/actions.navigation';
import { defaultContentControlWebState } from './customStates/contentControl/content-control.slice';
import {
  setAvailableSchedulersLoadedForContentControlWeb,
  setAvailableSchedulersLoadedForContentControlWebActionHandler,
} from './customStates/contentControl/content-control.actions';
import _ from 'lodash';
import {
  setIsLoading,
  setIsLoadingActionHandler,
  setIsSaving,
  setIsSavingActionHandler,
  setIsWaitingValidation,
  setIsWaitingValidationActionHandler,
  setSaveRestriction,
  setSaveRestrictionActionHandler,
} from './saveButtonSlice/actions.save';
import { PolicySaveRestriction } from './saveButtonSlice/types.save';
import { defaultAntimalwareSettingsState } from './customStates/antimalware/antimalware.slice';
import {
  setExclusionsListsLoadedForAntimalwareSettings,
  setExclusionsListsLoadedForAntimalwareSettingsActionHandler,
  setVendorsExclusionsListsLoadedForAntimalwareSettings,
  setVendorsExclusionsListsLoadedForAntimalwareSettingsActionHandler,
} from './customStates/antimalware/antimalware.actions';
import {
  setValidationLoadedForGeneralDetails,
  setValidationLoadedForGeneralDetailsActionHandler,
  setValidationLoadedForGeneralSecurityTelemetry,
  setValidationLoadedForGeneralSecurityTelemetryActionHandler,
} from './customStates/general/general.actions';
import { defaultGeneralDetailsState, defaultGeneralSecurityTelemetryState } from './customStates/general/general.slice';

export const defaultSectionState: SectionState = { isValid: true, status: { type: SectionStatusType.NONE } };
export function getDefaultModulesState() {
  return {
    [ModuleId.POLICY_SETTINGS]: {
      sections: {
        [SectionId.GENERAL_INHERITANCE]: _.cloneDeep(defaultSectionState),
        [SectionId.DETAILS]: _.cloneDeep(defaultGeneralDetailsState),
      },
    },
    [ModuleId.GENERAL]: {
      sections: {
        [SectionId.GENERAL_NOTIFICATIONS]: _.cloneDeep(defaultSectionState),
        [SectionId.SECURITY_TELEMETRY]: _.cloneDeep(defaultGeneralSecurityTelemetryState),
        [SectionId.COMMUNICATION]: _.cloneDeep(defaultSectionState),
        [SectionId.UPDATE]: _.cloneDeep(defaultSectionState),
        [SectionId.SETTINGS]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.SANDBOX]: {
      sections: {
        [SectionId.ENDPOINT_SENSOR]: _.cloneDeep(defaultSectionState),
        [SectionId.NETWORK_SENSOR]: _.cloneDeep(defaultSectionState),
        [SectionId.ICAP_SENSOR]: _.cloneDeep(defaultSectionState),
        [SectionId.SANDBOX_MANAGER]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.PATCH_MANAGEMENT]: {
      sections: {
        [SectionId.NONE]: _.cloneDeep(defaultPatchManagementState),
      },
    },
    [ModuleId.INTEGRITY_MONITOR]: {
      sections: {
        [SectionId.INTEGRITY_MONITOR_REAL_TIME]: _.cloneDeep(defaultIntegrityMonitorRealTimeState),
      },
    },
    [ModuleId.RELAY]: {
      sections: {
        [SectionId.COMMUNICATION]: _.cloneDeep(defaultSectionState),
        [SectionId.UPDATE]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.EDR_SENSOR]: {
      sections: {
        [SectionId.GENERAL]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.ENCRYPTION]: {
      sections: {
        [SectionId.GENERAL]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.STORAGE_PROTECTION]: {
      sections: {
        [SectionId.STORAGE_PROTECTION_ICAP]: _.cloneDeep(defaultSectionState),
        [SectionId.EXCLUSIONS]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.INDICATORS_OF_RISK]: {
      sections: {
        [SectionId.NONE]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.DEVICE_CONTROL]: {
      sections: {
        [SectionId.RULES]: _.cloneDeep(defaultSectionState),
        [SectionId.EXCLUSIONS]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.LIVE_SEARCH]: {
      sections: {
        [SectionId.NONE]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.APPLICATION_CONTROL]: {
      sections: {
        [SectionId.NONE]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.NSX]: {
      sections: {
        [SectionId.NONE]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.FIREWALL]: {
      sections: {
        [SectionId.SETTINGS]: _.cloneDeep(defaultSectionState),
        [SectionId.FIREWALL_NETWORK]: _.cloneDeep(defaultSectionState),
        [SectionId.FIREWALL_ADVANCED]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.CONTENT_CONTROL]: {
      sections: {
        [SectionId.CONTENT_CONTROL_TRAFFIC]: _.cloneDeep(defaultSectionState),
        [SectionId.CONTENT_CONTROL_WEB]: _.cloneDeep(defaultContentControlWebState),
        [SectionId.CONTENT_CONTROL_DATA_PROTECTION]: _.cloneDeep(defaultSectionState),
        [SectionId.CONTENT_CONTROL_NETWORK_MONITOR]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.EXCHANGE_PROTECTION]: {
      sections: {
        [SectionId.GENERAL]: _.cloneDeep(defaultSectionState),
        [SectionId.EXCHANGE_ANTIMALWARE]: _.cloneDeep(defaultSectionState),
        [SectionId.EXCHANGE_CONTENT_CONTROL]: _.cloneDeep(defaultSectionState),
        [SectionId.EXCHANGE_ANTISPAM]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.ANTIMALWARE]: {
      sections: {
        [SectionId.EXCLUSIONS]: _.cloneDeep(defaultAntimalwareSettingsState),
        [SectionId.ANTIMALWARE_ON_ACCESS]: _.cloneDeep(defaultSectionState),
        [SectionId.ANTIMALWARE_ON_EXECUTE]: _.cloneDeep(defaultSectionState),
        [SectionId.ANTIMALWARE_ON_DEMAND]: _.cloneDeep(defaultSectionState),
        [SectionId.ANTIMALWARE_ANTI_TAMPERING]: _.cloneDeep(defaultSectionState),
        [SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE]: _.cloneDeep(defaultSectionState),
        [SectionId.ANTIMALWARE_ANTI_EXPLOIT]: _.cloneDeep(defaultSectionState),
        [SectionId.SETTINGS]: _.cloneDeep(defaultSectionState),
        [SectionId.ANTIMALWARE_SECURITY_SERVERS]: _.cloneDeep(defaultSectionState),
      },
    },
    [ModuleId.BLOCKLIST_RULES]: {
      sections: {
        [SectionId.NONE]: _.cloneDeep(defaultSectionState),
      },
    },
  };
}

/**
 * Important: Any new state added to the PolicyAppState should be added to the reset function.
 */
export const initialState: PolicyAppState = {
  navigationHeaderSlice: {
    policyName: '',
    context: PolicyContext.ADD,
    updateNeeded: false,
  },
  phantom: false,
  modules: {}, // available modules with available sections
  isPolicyDataLoaded: false,

  saveButtonSlice: {
    restriction: PolicySaveRestriction.NONE,
    isLoading: true, // waiting for data from backend
    isSaving: false, // waiting for saving data
    isWaitingValidation: false, // waiting for remote validation
  },

  accordionsSlice: {
    status: {
      fullUpdateNeeded: false,
      pathUpdateNeeded: false,
    },
  },
};

export const policiesSlice = createSlice({
  name: 'policiesApp',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(reset, resetActionHandler);
    builder.addCase(setInitialTreeState, setInitialTreeStateActionHandler);
    builder.addCase(setPolicyDataLoaded, setPolicyDataLoadedActionHandler);
    builder.addCase(setIsLoading, setIsLoadingActionHandler);
    builder.addCase(setIsSaving, setIsSavingActionHandler);
    builder.addCase(setIsWaitingValidation, setIsWaitingValidationActionHandler);
    builder.addCase(setSaveRestriction, setSaveRestrictionActionHandler);
    builder.addCase(setSectionValidity, setSectionValidityActionHandler);
    builder.addCase(setSectionMask, setSectionMaskActionHandler);
    builder.addCase(clearSectionMask, clearSectionMaskActionHandler);

    // status
    builder.addCase(setIsStatusFullUpdateNeeded, setIsStatusFullUpdateNeededActionHandler);
    builder.addCase(setIsStatusPathUpdateNeeded, setIsStatusPathUpdateNeededActionHandler);
    builder.addCase(setPathToBeUpdatedWithStatus, setPathToBeUpdatedWithStatusActionHandler);
    builder.addCase(setPathStatuses, setPathStatusesActionHandler);

    // navigation header reducers
    builder.addCase(setPolicyName, setPolicyNameActionHandler);
    builder.addCase(setNavigationHeaderSlice, setNavigationHeaderSliceActionHandler);

    // patch management reducers
    builder.addCase(setIsLoadingForPatchSummaryWindow, setIsLoadingForPatchSummaryWindowActionHandler);
    builder.addCase(setSelectedIdsForPatchSummaryWindow, setSelectedIdsForPatchSummaryWindowActionHandler);
    builder.addCase(
      setAvailableMaintenanceWindowsLoadedForPatch,
      setAvailableMaintenanceWindowsLoadedForPatchActionHandler
    );

    // integrity monitor reducers
    builder.addCase(
      setCompanyRuleSetsLoadedForIntegrityMonitor,
      setCompanyRuleSetsLoadedForIntegrityMonitorActionHandler
    );
    builder.addCase(
      setSelectedRuleSetsLoadedForIntegrityMonitor,
      setSelectedRuleSetsLoadedForIntegrityMonitorActionHandler
    );

    // content control reducers
    builder.addCase(
      setAvailableSchedulersLoadedForContentControlWeb,
      setAvailableSchedulersLoadedForContentControlWebActionHandler
    );

    // antimalware reducers
    builder.addCase(
      setExclusionsListsLoadedForAntimalwareSettings,
      setExclusionsListsLoadedForAntimalwareSettingsActionHandler
    );
    builder.addCase(
      setVendorsExclusionsListsLoadedForAntimalwareSettings,
      setVendorsExclusionsListsLoadedForAntimalwareSettingsActionHandler
    );
    builder.addCase(setValidationLoadedForGeneralDetails, setValidationLoadedForGeneralDetailsActionHandler);
    builder.addCase(
      setValidationLoadedForGeneralSecurityTelemetry,
      setValidationLoadedForGeneralSecurityTelemetryActionHandler
    );
  },
});

export const selectPoliciesState = (state: RootState) => state.policiesApp;
export const policiesAppReducer = policiesSlice.reducer;
